import { selector } from 'recoil';

import { supabase } from '../utils/supabaseClient';
import sessionStorage from './sessionStorage';

/**
 * The partner list, read straight from PostgREST.
 *
 * `public.partners` grants read access to the `authenticated` Postgres role
 * only — every SELECT policy on the table is scoped to it. When auth-js holds
 * no usable session, supabase-js signs the request with the public anon key
 * instead, PostgREST runs it as `anon`, and Postgres rejects it with
 * "permission denied for table partners" (Sentry ARTIC-HUB-15D /
 * ARTIC-HUB-16D). `PartnerPicker` lives in the sidebar header, so that request
 * fired on every page for anyone whose token had lapsed — a token refresh that
 * the ARTIC-HUB-12X kill switch short-circuits leaves Recoil's session atom
 * populated (RequireAuth still passes) while auth-js has already dropped the
 * session underneath it.
 *
 * So gate on the session auth-js will actually sign with, and take the session
 * atom as a dependency so the list is re-read when the session lands rather
 * than serving a cached signed-out result for the rest of the page load.
 * `setSession` hands the atom a fresh object on every token refresh, so that
 * dependency also costs one extra read an hour — the price of the selector
 * being able to heal itself, and only Artic staff see the picker it feeds.
 */
export const partnersQuery = selector({
  key: 'PartnersList',
  get: async ({ get }) => {
    if (!get(sessionStorage)) return null;
    if (!(await hasUsableSession())) return null;

    const response = await supabase.from('partners').select('*');
    if (response.error) {
      // A signed-in read that still fails is a real fault, not an empty
      // partner list. Throwing here would take the whole page to
      // ErrorPage500 via the router's errorElement, so report and degrade.
      // Only `code` and `message` are logged — PostgREST puts row values in
      // `details`/`hint`, and this line becomes a Sentry breadcrumb.
      const { code, message } = response.error;
      console.error('[partnersQuery] failed to read partners', { code, message });
      return null;
    }
    return response.data;
  },
});

/**
 * Whether supabase-js can sign the next PostgREST call as `authenticated`.
 * `getSession()` returns null once the session is gone and rejects when the
 * refresh kill switch is latched; both mean the same thing here.
 */
async function hasUsableSession(): Promise<boolean> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session !== null;
  } catch {
    return false;
  }
}
